import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Loader2, Send, RefreshCw, AlertTriangle, Calendar, Check, DollarSign, ChevronDown, History } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { createBillApproval } from "@/lib/createBillApproval";
import { cn } from "@/lib/utils";
import { useBillPaymentLog } from "@/hooks/useBillPaymentLog";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface Props {
  wo: any;
  projectId: string;
  onMutate: () => void;
  onOpenCreateBill: () => void;
}

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  draft:          { label: "Draft Bill",             className: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" },
  submitted:      { label: "Pending Approval",        className: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300" },
  sent:           { label: "Pending Approval",        className: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300" },
  approved:       { label: "Approved",               className: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" },
  scheduled:      { label: "Scheduled for Payment",  className: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" },
  paid:           { label: "Paid",                   className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300" },
  partially_paid: { label: "Partially Paid",         className: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300" },
  rejected:       { label: "Rejected",               className: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300" },
  void:           { label: "Void",                   className: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400" },
  voided:         { label: "Voided",                 className: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400" },
};

const PAYMENT_TERMS_OPTIONS = [
  { value: "net_7",  label: "Net 7 (7 days)" },
  { value: "net_14", label: "Net 14 (14 days)" },
  { value: "net_20", label: "Net 20 (20 days)" },
  { value: "net_30", label: "Net 30 (30 days)" },
];

const ACTION_LABEL: Record<string, string> = {
  approved: "Approved",
  paid: "Paid",
  partially_paid: "Partial Payment",
};

function BillPaymentHistory({ billId }: { billId: string }) {
  const { data: logs, isLoading } = useBillPaymentLog(billId);

  if (isLoading || !logs || logs.length === 0) return null;

  return (
    <Collapsible>
      <CollapsibleTrigger className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors group">
        <History className="h-3.5 w-3.5" />
        <span>Payment History ({logs.length})</span>
        <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="text-left px-2 py-1.5 font-medium">Action</th>
                <th className="text-left px-2 py-1.5 font-medium">Line Item</th>
                <th className="text-right px-2 py-1.5 font-medium">Line Amt</th>
                <th className="text-right px-2 py-1.5 font-medium">Paid</th>
                <th className="text-left px-2 py-1.5 font-medium">Method</th>
                <th className="text-left px-2 py-1.5 font-medium">Reference</th>
                <th className="text-left px-2 py-1.5 font-medium">Date</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-b last:border-0">
                  <td className="px-2 py-1.5">
                    <Badge variant="outline" className="text-[10px]">
                      {ACTION_LABEL[log.action] ?? log.action}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5 max-w-[200px] truncate" title={log.line_description ?? ""}>
                    {log.line_description || "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(Number(log.line_amount))}</td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {Number(log.payment_amount) > 0 ? fmt(Number(log.payment_amount)) : "—"}
                  </td>
                  <td className="px-2 py-1.5">{log.payment_method?.toUpperCase() ?? "—"}</td>
                  <td className="px-2 py-1.5 truncate max-w-[120px]" title={log.payment_reference ?? ""}>
                    {log.payment_reference || "—"}
                  </td>
                  <td className="px-2 py-1.5 text-muted-foreground">
                    {log.payment_date ? new Date(log.payment_date).toLocaleDateString() : new Date(log.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export const WOBillsList = ({ wo, projectId, onMutate, onOpenCreateBill }: Props) => {
  const queryClient = useQueryClient();
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [schedulingBill, setSchedulingBill] = useState<any | null>(null);
  const [paymentTerms, setPaymentTerms] = useState("net_30");
  const [payingBill, setPayingBill] = useState<any | null>(null);
  const [payDate, setPayDate] = useState(new Date().toISOString().split("T")[0]);
  const [payRef, setPayRef] = useState("");
  const [payMethod, setPayMethod] = useState("ach");
  const [payAmount, setPayAmount] = useState("");
  const [payingLoading, setPayingLoading] = useState(false);

  const { data: bills, isLoading } = useQuery({
    queryKey: ["wo_bills", wo.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bills")
        .select("id, bill_number, bill_date, status, subtotal, retainage_amount, total, description, rejection_reason, revision_number, sent_at, created_at, payment_terms, payment_due_date, scheduled_at, paid_at, amount_paid, payment_method")
        .eq("work_order_id", wo.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const handleSendForApproval = async (bill: any) => {
    setSendingId(bill.id);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");
      const userId = (await supabase.auth.getUser()).data.user?.id;

      const { error: updateErr } = await supabase
        .from("bills")
        .update({
          status: "sent",
          sent_at: new Date().toISOString(),
          sent_by: userId,
        } as any)
        .eq("id", bill.id);
      if (updateErr) throw updateErr;

      await createBillApproval({
        companyId,
        requestType: "bill_approve",
        title: `Bill ${bill.bill_number} — ${wo.number}`,
        projectId,
        linkedWoId: wo.id,
        linkedBillId: bill.id,
        requestData: {
          subtotal: Number(bill.subtotal),
          retainage: Number(bill.retainage_amount),
          total: Number(bill.total),
          revision_number: bill.revision_number ?? 0,
        },
        createdBy: userId,
      });

      await supabase.from("ops_audit_log").insert({
        table_name: "bills",
        record_id: bill.id,
        action: "bill_sent_for_approval",
        prev_status: "draft",
        new_status: "sent",
        changed_by: userId,
        metadata: {
          wo_number: wo.number,
          bill_number: bill.bill_number,
          total: Number(bill.total),
        },
      } as any);

      toast.success(`${bill.bill_number} sent for approval`);
      queryClient.invalidateQueries({ queryKey: ["wo_bills", wo.id] });
      onMutate();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSendingId(null);
    }
  };

  const handleSchedulePayment = async () => {
    if (!schedulingBill) return;
    try {
      const { error } = await supabase.rpc("schedule_bill" as any, {
        p_bill_id: schedulingBill.id,
        p_payment_terms: paymentTerms,
      } as any);
      if (error) {
        const termsMap: Record<string, number> = { net_7: 7, net_14: 14, net_20: 20, net_30: 30 };
        const days = termsMap[paymentTerms] ?? 30;
        const dueDate = new Date();
        dueDate.setDate(dueDate.getDate() + days);
        const { error: updateErr } = await supabase
          .from("bills")
          .update({
            status: "scheduled",
            payment_terms: paymentTerms,
            payment_due_date: dueDate.toISOString().split("T")[0],
            scheduled_at: new Date().toISOString(),
          } as any)
          .eq("id", schedulingBill.id);
        if (updateErr) throw updateErr;
      }
      toast.success(`${schedulingBill.bill_number} scheduled for payment`);
      queryClient.invalidateQueries({ queryKey: ["wo_bills", wo.id] });
      onMutate();
      setSchedulingBill(null);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handlePaySubmit = async () => {
    if (!payingBill) return;
    setPayingLoading(true);
    try {
      const billTotal = Number(payingBill.total ?? 0);
      const amt = payAmount === "" ? billTotal : Number(payAmount);
      const finalStatus = amt < billTotal ? "partially_paid" : "paid";
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const { data: companyId } = await supabase.rpc("my_company_id");

      const { error } = await supabase
        .from("bills")
        .update({
          status: finalStatus,
          paid_at: new Date(payDate).toISOString(),
          paid_by: userId,
          payment_reference: payRef.trim() || null,
          payment_method: payMethod,
          amount_paid: amt,
        } as any)
        .eq("id", payingBill.id);
      if (error) throw error;

      try {
        await supabase.from("ops_audit_log").insert({
          table_name: "bills", record_id: payingBill.id,
          action: finalStatus === "paid" ? "bill_paid" : "bill_partially_paid",
          prev_status: payingBill.status, new_status: finalStatus,
          changed_by: userId,
          metadata: { payment_reference: payRef.trim() || null, payment_method: payMethod, amount_paid: amt },
        } as any);
      } catch { /* non-blocking */ }

      // Per-line payment log
      if (companyId) {
        try {
          const { data: lines } = await supabase
            .from("bill_lines")
            .select("id, description, line_total")
            .eq("bill_id", payingBill.id);
          if (lines && lines.length > 0) {
            const rows = lines.map((line: any) => {
              const lineTotal = Number(line.line_total ?? 0);
              const proportion = billTotal > 0 ? lineTotal / billTotal : 0;
              return {
                company_id: companyId,
                bill_id: payingBill.id,
                bill_line_id: line.id,
                action: finalStatus,
                line_description: line.description ?? null,
                line_amount: lineTotal,
                payment_amount: Number((amt * proportion).toFixed(2)),
                payment_method: payMethod,
                payment_reference: payRef.trim() || null,
                payment_date: new Date(payDate).toISOString(),
                recorded_by: userId ?? null,
              };
            });
            await supabase.from("bill_payment_log").insert(rows as any);
          }
        } catch { /* non-blocking */ }
      }

      toast.success(`${payingBill.bill_number} ${finalStatus === "partially_paid" ? "partially " : ""}marked as paid`);
      queryClient.invalidateQueries({ queryKey: ["wo_bills", wo.id] });
      queryClient.invalidateQueries({ queryKey: ["bills"] });
      queryClient.invalidateQueries({ queryKey: ["bill_payment_log"] });
      queryClient.invalidateQueries({ queryKey: ["project_expenses"] });
      queryClient.invalidateQueries({ queryKey: ["project_actual_cost"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_metrics"] });
      onMutate();
      setPayingBill(null);
      setPayRef("");
      setPayAmount("");
      setPayMethod("ach");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setPayingLoading(false);
    }
  };

  const openPayDialog = (bill: any) => {
    setPayingBill(bill);
    setPayDate(new Date().toISOString().split("T")[0]);
    setPayRef("");
    setPayAmount("");
    setPayMethod("ach");
  };

  const handleReviseAndResubmit = (bill: any) => {
    toast.info(`Revising ${bill.bill_number} — WO lines restored. Create a new bill with corrections.`);
    onOpenCreateBill();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!bills || bills.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        No bills created for this work order yet.
      </div>
    );
  }

  const payBillTotal = Number(payingBill?.total ?? 0);
  const payEffective = payAmount === "" ? payBillTotal : Number(payAmount);
  const payIsPartial = payEffective > 0 && payEffective < payBillTotal;

  return (
    <div className="space-y-3">
      {/* Schedule Payment Dialog */}
      <Dialog open={!!schedulingBill} onOpenChange={(o) => !o && setSchedulingBill(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Schedule Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Bill <span className="font-mono font-semibold">{schedulingBill?.bill_number}</span> · Total {fmt(Number(schedulingBill?.total ?? 0))}
            </p>
            <div>
              <Label>Payment Terms</Label>
              <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_TERMS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedulingBill(null)}>Cancel</Button>
            <Button onClick={handleSchedulePayment}>
              <Calendar className="h-4 w-4 mr-1" /> Schedule Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={!!payingBill} onOpenChange={(o) => !o && setPayingBill(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Bill <span className="font-mono font-semibold">{payingBill?.bill_number}</span> — Total {fmt(payBillTotal)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Payment Date</Label>
              <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Payment Method</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={payMethod}
                onChange={(e) => setPayMethod(e.target.value)}
              >
                <option value="ach">ACH</option>
                <option value="check">Check</option>
                <option value="wire">Wire Transfer</option>
                <option value="credit_card">Credit Card</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Amount</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                max={payBillTotal}
                placeholder={fmt(payBillTotal)}
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
              />
              {payIsPartial && (
                <p className="text-xs text-amber-600">Partial payment — bill will be marked as partially paid.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Payment Reference</Label>
              <Input placeholder="Check #, confirmation, etc." value={payRef} onChange={(e) => setPayRef(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayingBill(null)}>Cancel</Button>
            <Button onClick={handlePaySubmit} disabled={payingLoading || payEffective <= 0}>
              <DollarSign className="h-4 w-4 mr-1" />
              {payingLoading ? "Saving…" : payIsPartial ? "Record Partial Payment" : "Confirm Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {bills.map((bill: any) => {
        const status = bill.status as string;
        const badge = STATUS_BADGE[status] ?? STATUS_BADGE.draft;
        const isSending = sendingId === bill.id;

        return (
          <div
            key={bill.id}
            className={cn(
              "border rounded-md p-3 space-y-2",
              status === "rejected" && "border-red-300 bg-red-50/50 dark:border-red-800 dark:bg-red-950/30",
              status === "approved" && "border-green-200 bg-green-50/30 dark:border-green-800 dark:bg-green-950/20",
              status === "scheduled" && "border-blue-200 bg-blue-50/30 dark:border-blue-800 dark:bg-blue-950/20",
            )}
          >
            {/* Header row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-medium">{bill.bill_number}</span>
                <Badge variant="outline" className={cn("text-[10px]", badge.className)}>
                  {badge.label}
                </Badge>
                {(bill.revision_number ?? 0) > 0 && (
                  <Badge variant="outline" className="text-[10px] bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                    Rev {bill.revision_number}
                  </Badge>
                )}
              </div>
              <span className="text-sm text-muted-foreground">{bill.bill_date}</span>
            </div>

            {/* Amounts */}
            <div className="flex gap-4 text-sm">
              <span>Subtotal: <span className="font-medium">{fmt(Number(bill.subtotal))}</span></span>
              <span>Retainage: <span className="text-muted-foreground">{fmt(Number(bill.retainage_amount))}</span></span>
              <span>Total: <span className="font-semibold">{fmt(Number(bill.total))}</span></span>
              {Number(bill.amount_paid) > 0 && (
                <span>Paid: <span className="font-medium text-emerald-600">{fmt(Number(bill.amount_paid))}</span></span>
              )}
            </div>

            {/* Payment info for scheduled/paid bills */}
            {(status === "scheduled" || status === "paid" || status === "partially_paid") && bill.payment_due_date && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                <span>{bill.payment_terms?.replace("_", " ").toUpperCase()} · Due {bill.payment_due_date}</span>
                {(status === "paid" || status === "partially_paid") && bill.paid_at && (
                  <span className="text-emerald-600 font-medium">
                    · {bill.payment_method?.toUpperCase()} · {new Date(bill.paid_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            )}

            {/* Rejection reason */}
            {status === "rejected" && bill.rejection_reason && (
              <div className="flex items-start gap-2 text-sm bg-red-100/50 dark:bg-red-900/30 rounded p-2">
                <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-red-700 dark:text-red-400">Rejection reason: </span>
                  <span className="text-red-600 dark:text-red-300">{bill.rejection_reason}</span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-1 flex-wrap">
              {status === "draft" && (
                <Button size="sm" variant="default" onClick={() => handleSendForApproval(bill)} disabled={isSending}>
                  {isSending ? (
                    <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Sending...</>
                  ) : (
                    <><Send className="mr-1 h-3.5 w-3.5" />Send for Approval</>
                  )}
                </Button>
              )}
              {status === "approved" && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400"
                    onClick={() => { setSchedulingBill(bill); setPaymentTerms("net_30"); }}
                  >
                    <Calendar className="mr-1 h-3.5 w-3.5" />
                    Schedule Payment
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-400"
                    onClick={() => openPayDialog(bill)}
                  >
                    <DollarSign className="mr-1 h-3.5 w-3.5" />
                    Mark as Paid
                  </Button>
                </>
              )}
              {(status === "scheduled" || status === "partially_paid") && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-400"
                  onClick={() => openPayDialog(bill)}
                >
                  <DollarSign className="mr-1 h-3.5 w-3.5" />
                  {status === "partially_paid" ? "Record Additional Payment" : "Mark as Paid"}
                </Button>
              )}
              {status === "rejected" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950"
                  onClick={() => handleReviseAndResubmit(bill)}
                >
                  <RefreshCw className="mr-1 h-3.5 w-3.5" />
                  Revise & Resubmit
                </Button>
              )}
            </div>

            {/* Payment History */}
            {(status === "approved" || status === "paid" || status === "partially_paid" || status === "scheduled") && (
              <BillPaymentHistory billId={bill.id} />
            )}
          </div>
        );
      })}
    </div>
  );
};
